// Audio graph shared by sound effects, interface sounds and the ride ambience: one lazily created context, three
// buses with their own volume, a stereo reverb with pre-delay, and a mastering chain (sub-sonic cut, glue
// compressor, brick-wall limiter) so stacked effects never clip. Everything is synthesised: no audio files.
import { state } from '../data/store.js';

export interface Bus {
  ac: AudioContext;
  master: GainNode;
  /** Race effects (items, hits, countdown). */
  sfx: GainNode;
  /** Interface sounds (clicks, tours, end screen). */
  ui: GainNode;
  /** Riding sounds: wind, tyres, chain, the bunch and the roadside crowd. Ducked under important effects. */
  amb: GainNode;
  /** Reverb send. */
  verb: GainNode;
  /** Two seconds of mono white noise, shared by every noisy voice. */
  noise: AudioBuffer;
  /** Two seconds of stereo noise, decorrelated between the ears: wide, natural wind and crowd beds. */
  wide: AudioBuffer;
  /** Gain of the ambience ducker (1: no ducking). */
  duck: GainNode;
}

let bus: Bus | null = null;
let failed = false;

const unit = (v: unknown, dflt: number): number => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : dflt);

/** Master volume from the settings, bounded: a hand-edited localStorage value must not blow the speakers. */
export const masterVolume = (v: unknown): number => unit(v, 0.7);

export interface MixLevels { master: number; sfx: number; amb: number }

/**
 * Volume of each bus from the settings, each bounded to 0-1 with a default. The old "riding sounds" switch, when
 * off, still means silent riding sounds.
 */
export function mixLevels(s: { volume?: unknown; sfxVolume?: unknown; ambVolume?: unknown; ambience?: unknown }): MixLevels {
  return { master: masterVolume(s.volume), sfx: unit(s.sfxVolume, 0.9), amb: s.ambience === false && s.ambVolume === undefined ? 0 : unit(s.ambVolume, 0.8) };
}

/** Perceived loudness follows the square of a slider better than the slider itself: half way sounds half as loud. */
export const sliderGain = (x: number): number => x * x;

/** Stereo noise, decorrelated per channel. */
function noiseBuffer(ac: BaseAudioContext, channels: number, seconds: number): AudioBuffer {
  const b = ac.createBuffer(channels, Math.floor(ac.sampleRate * seconds), ac.sampleRate);
  for (let c = 0; c < channels; c++) { const d = b.getChannelData(c); for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1; }
  return b;
}

/**
 * Stereo impulse: a few early reflections (a road between buildings), then a dense tail of filtered noise that
 * darkens as it decays, like a real outdoor space. Each ear gets its own noise, which makes the reverb wide.
 */
function impulse(ac: AudioContext, seconds: number): AudioBuffer {
  const sr = ac.sampleRate, n = Math.floor(sr * seconds), b = ac.createBuffer(2, n, sr);
  const early = [[0.011, 0.5], [0.019, 0.38], [0.027, 0.3], [0.041, 0.22], [0.058, 0.16]];
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const k = i / n, a = 0.5 - 0.42 * k; // the low-pass closes over time: highs die first
      lp += a * ((Math.random() * 2 - 1) - lp);
      // the tail fades in over 30 ms so the early reflections stand out
      d[i] = lp * Math.pow(1 - k, 2.6) * Math.min(1, i / (sr * 0.03)) * 0.6;
    }
    for (const [at, g] of early) {
      const i = Math.floor((at + (c ? 0.0023 : 0)) * sr); // a slightly different path to each ear
      if (i < n) d[i] += g * (c ? -1 : 1);
    }
  }
  return b;
}

function build(): Bus {
  const ac = new AudioContext({ latencyHint: 'interactive' });
  const master = ac.createGain();
  // mastering: cut what speakers cannot play, glue the mix, then a limiter as a safety ceiling
  const sub = ac.createBiquadFilter(); sub.type = 'highpass'; sub.frequency.value = 28; sub.Q.value = 0.7;
  const glue = ac.createDynamicsCompressor();
  glue.threshold.value = -18; glue.knee.value = 10; glue.ratio.value = 2.5; glue.attack.value = 0.01; glue.release.value = 0.25;
  const limit = ac.createDynamicsCompressor();
  limit.threshold.value = -3; limit.knee.value = 0; limit.ratio.value = 20; limit.attack.value = 0.001; limit.release.value = 0.08;
  master.connect(sub).connect(glue).connect(limit).connect(ac.destination);

  const sfx = ac.createGain(), ui = ac.createGain(), amb = ac.createGain(), duck = ac.createGain(), verb = ac.createGain();
  sfx.connect(master); ui.connect(master); amb.connect(duck).connect(master);
  // reverb send: pre-delay, low cut (a muddy bass reverb is what makes amateur mixes boomy), convolver, return
  const pre = ac.createDelay(0.1), lowCut = ac.createBiquadFilter(), conv = ac.createConvolver(), ret = ac.createGain();
  pre.delayTime.value = 0.018; lowCut.type = 'highpass'; lowCut.frequency.value = 320; conv.buffer = impulse(ac, 1.6); ret.gain.value = 0.22;
  verb.connect(pre).connect(lowCut).connect(conv).connect(ret).connect(master);

  const b: Bus = { ac, master, sfx, ui, amb, verb, duck, noise: noiseBuffer(ac, 1, 2), wide: noiseBuffer(ac, 2, 2) };
  keepRunning(ac, document);
  return b;
}

/** The part of `document` keepRunning watches: a test passes its own. */
export interface PageVisibility {
  hidden: boolean;
  addEventListener(type: 'visibilitychange', fn: () => void): void;
  removeEventListener(type: 'visibilitychange', fn: () => void): void;
}

/**
 * A ride goes on while its tab is hidden (ui/ride/loop.ts), and so does its sound: the context is never suspended on
 * purpose. One the browser suspended on its own (an interruption, a policy) is resumed as soon as the page is seen
 * again, since the audio clock stands still meanwhile and nothing timed on it could ask. Returns what stops watching.
 */
export function keepRunning(ac: Pick<AudioContext, 'state' | 'resume'>, doc: PageVisibility): () => void {
  const check = () => { if (!doc.hidden && ac.state === 'suspended') ac.resume().catch(() => {}); };
  doc.addEventListener('visibilitychange', check);
  return () => doc.removeEventListener('visibilitychange', check);
}

/** Applies the volume settings to the buses (glides, so a slider never clicks). */
function applyLevels(b: Bus) {
  const L = mixLevels(state.settings), t = b.ac.currentTime;
  b.master.gain.setTargetAtTime(sliderGain(L.master) * 1.4, t, 0.02);
  b.sfx.gain.setTargetAtTime(sliderGain(L.sfx), t, 0.02);
  b.ui.gain.setTargetAtTime(sliderGain(L.sfx) * 0.8, t, 0.02);
  b.amb.gain.setTargetAtTime(sliderGain(L.amb), t, 0.02);
}

const onCreate: ((b: Bus) => void)[] = [];
/** Runs once the audio bus exists (the sound bank prepares its sounds then). */
export function whenAudio(fn: (b: Bus) => void) { if (bus) fn(bus); else onCreate.push(fn); }

/** The audio bus, created on first use (after a user gesture) and resumed if the browser suspended it. Null without WebAudio. */
export function audio(): Bus | null {
  if (failed) return null;
  if (!bus) {
    try { bus = build(); } catch { failed = true; return null; }
    for (const fn of onCreate.splice(0)) fn(bus);
  }
  if (bus.ac.state === 'suspended') bus.ac.resume().catch(() => {});
  applyLevels(bus);
  return bus;
}

/**
 * Ducking: the riding sounds dip under an important effect (the start, a crash, the finish) and come back, the way
 * a broadcast mix keeps the commentary clear. `depth` 0-1 is how far they dip.
 */
export function duck(b: Bus, depth: number, hold: number) {
  const t = b.ac.currentTime, g = b.duck.gain, to = Math.max(0.2, 1 - Math.min(0.8, Math.max(0, depth)));
  // hold the current value before re-planning: cancelling alone would jump back to the last set point (a click)
  if (g.cancelAndHoldAtTime) g.cancelAndHoldAtTime(t); else { const v = g.value; g.cancelScheduledValues(t); g.setValueAtTime(v, t); }
  g.setTargetAtTime(to, t, 0.015);
  g.setTargetAtTime(1, t + Math.max(0.05, hold), 0.25);
}
